import BulletList from '@tiptap/extension-bullet-list'
import ListItem from '@tiptap/extension-list-item'
import Underline from '@tiptap/extension-underline'
import { useEditor, type JSONContent } from '@tiptap/vue-3'
import type { Editor } from '@tiptap/core'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Heading, { type Level } from '@tiptap/extension-heading'
import Bold from '@tiptap/extension-bold'
import Strike from '@tiptap/extension-strike'
import History from '@tiptap/extension-history'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Placeholder from '@tiptap/extension-placeholder'

export const useWysiwygEditor = (
  initialValue?: JSONContent,
  isReadonly?: boolean,
  placeholder?: string,
  callback?: (value: JSONContent | undefined) => void,
  paragraphClass?: string,
) => {
  const { t } = useI18n()

  const emitEditorValue = (currentEditor: Editor) => {
    if (!callback) return
    callback(currentEditor.isEmpty ? undefined : currentEditor.getJSON())
  }

  const fontList: { label: string; level: Level }[] = [
    {
      label: t('components.wysiwyg.font.heading1'),
      level: 1,
    },
    {
      label: t('components.wysiwyg.font.heading2'),
      level: 2,
    },
    {
      label: t('components.wysiwyg.font.heading3'),
      level: 3,
    },
    {
      label: t('components.wysiwyg.font.paragraph'),
      level: 4,
    },
  ]

  const editor = useEditor({
    content: initialValue,
    editable: !isReadonly,
    extensions: [
      Document,
      Paragraph.configure({
        HTMLAttributes: {
          class: paragraphClass ?? 'text-body-large',
        },
      }),
      History,
      Underline,
      BulletList.configure({
        HTMLAttributes: {
          class: 'pl-4 list-disc',
        },
      }),
      ListItem,
      Text,
      Heading.configure({
        levels: fontList.map((font) => font.level),
      }),
      Bold,
      Strike,
      Placeholder.configure({
        placeholder: placeholder,
        emptyEditorClass: 'is-editor-empty',
        emptyNodeClass: 'is-empty',
        showOnlyCurrent: true,
      }),
    ],
    editorProps: {
      attributes: {
        class: !isReadonly
          ? 'w-full overflow-y-auto p-2 grow ring-0! ring-offset-0! outline-0! textfield'
          : '',
      },
    },
    onFocus: ({ editor }) => {
      emitEditorValue(editor)
    },
    onUpdate: ({ editor }) => {
      emitEditorValue(editor)
    },
  })

  const toggleBold = () => {
    if (!editor.value) return
    editor.value.chain().focus().toggleBold().run()
  }

  const toggleUnderline = () => {
    if (!editor.value) return
    editor.value.chain().focus().toggleUnderline().run()
  }

  const toggleBulletList = () => {
    if (!editor.value) return
    editor.value.chain().focus().toggleBulletList().run()
  }

  const toggleHeading = (level: Level) => {
    if (!editor.value) return
    editor.value.chain().focus().toggleHeading({ level: level }).run()
  }

  const selectedFont = computed(() => {
    if (!editor.value) return ''

    const heading = fontList.find(({ level }) => editor.value!.isActive('heading', { level }))
    return heading ? heading.label : t('components.wysiwyg.font.paragraph')
  })

  const convertJSONToPlainText = (json: JSON) => {
    editor.value?.commands.setContent(json)
    return editor.value?.getText()
  }

  return {
    toggleBold,
    toggleBulletList,
    toggleUnderline,
    toggleHeading,
    convertJSONToPlainText,
    selectedFont,
    editor,
    fontList,
  }
}
